import VendorRequests from 'features/vendors/vendor-requests/VendorRequests'
import React, { type ReactElement, useEffect } from 'react'
import { CssBaseline, Skeleton } from '@mui/material'
import { ThemeProvider, useTheme } from '@mui/material/styles'
import * as Sentry from '@sentry/react'
import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  Route,
  RouterProvider,
  useNavigate,
} from 'react-router-dom'
import { setErrorMessage } from 'store/app.slice'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import IncompleteAccount from 'features/authentication/incomplete-account'
import { RedirectIfAuth } from 'common/navigation/AuthGuards'
import { SNACKBAR_VARIANTS } from 'common/ui/theme-snackbar/ThemeSnackbar'
import VendorProfileLayout from 'features/vendor/vendor-profile-layout'
import SetPassword from 'features/authentication/reset-password'
import ForgotPassword from 'common/authentication/forgotPassword'
import Register from 'features/authentication/register/Register'
import ConfirmSignUp from 'common/authentication/confirmSignUp'
import InitialDataLoad from 'common/navigation/InitialDataLoad'
import RequireRoles from 'common/authentication/RequireRoles'
import { VendorStatus } from 'features/vendor/vendor.types'
import Activation from 'features/authentication/activation'
import Login from 'features/authentication/login/Login'
import AuthLayout from 'common/navigation/auth-layout'
import AppLayout from 'features/navigation/AppLayout'
import ThemeSnackbar from 'common/ui/theme-snackbar'
import theme from 'common/utils/theme'
import { Role, StoreStatus } from 'common/utils/types'
import { RequireVendorAuth } from 'features/navigation/AuthGuards'
import VendorProfile from 'features/vendor/vendor-profile/VendorProfile'
import LandingPage from 'features/authentication/landing-page'
import ProposalPage from 'features/bids/proposal-page'
import { getUserInfo } from 'common/authentication/auth.service'
import RegistrtationWizard from 'features/vendor/registration-wizard/RegistrationWizard'

import CrashScreen from './common/navigation/crash-screen'
import PageNotFound from './common/navigation/page-not-found'
import BidList from './features/bids/bid-list'
import VendorListContainer from './features/vendors/vendor-list-container/VendorListContainer'
import VerificationWrapper from './features/vendor/verification-profile/verification-wrapper/VerificationWrapper'
import UserNotAuthorized from './features/authentication/user-not-authorized'
import MyOffersContainer from './features/vendor/my-offers-container/MyOffersContainer'
import AcceptedBidDetails from './features/bids/accepted-bids/accepted-bid-details'
import AIQuestionnaire from './features/vendor/questionnaires/ai-questionnaires/AIQuestionnaire'
import AIResults from './features/vendor/questionnaires/ai-results/AIResults'
import ResetPassword from './common/authentication/resetPassword'
import styles from './App.module.scss'
import Answers from './features/vendors/vendor-list-container/answers'

const IndexReroute: React.FC = (): JSX.Element => {
  const navigate = useNavigate()
  const { userInfo } = useAppSelector((state) => state.auth)

  useEffect(() => {
    if (userInfo && userInfo.role === Role.admin) {
      navigate('lista', { replace: true })
    } else {
      navigate('profil', { replace: true })
    }
  }, [userInfo])

  return <div />
}

const VendorStatusCheck = (): JSX.Element => {
  const vendorStatus = useAppSelector((state) => state.vendor.vendor?.status)
  const vendorInitializationStatus = useAppSelector((state) => state.vendor.vendorInitializationStatus)
  const errorCode = useAppSelector((state) => state.vendor.errorCode)

  if (errorCode === 'person-type') {
    return <UserNotAuthorized />
  }

  if (vendorInitializationStatus === StoreStatus.Failed) {
    return <IncompleteAccount />
  }

  if (vendorStatus === VendorStatus.verified) {
    return (
      <VendorProfileLayout>
        <VendorProfile />
      </VendorProfileLayout>
    )
  } else {
    return <RegistrtationWizard />
  }
}

const AuthDataLoad = (): ReactElement => {
  const dispatch = useAppDispatch()
  const { palette } = useTheme()
  const { userInfo, getUserInfoStatus } = useAppSelector((state) => state.auth)

  useEffect(() => {
    if (!userInfo) {
      void dispatch(getUserInfo(true))
    }
  }, [userInfo])

  return getUserInfoStatus === StoreStatus.Succeeded ? (
    <Outlet />
  ) : (
    <div className={styles.loader}>
      <Skeleton height={150} width="100%" variant="rounded" sx={{ backgroundColor: palette.blue600.main }} />
      <Skeleton height={300} width="100%" variant="rounded" sx={{ backgroundColor: palette.blue600.main }} />
    </div>
  )
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      element={
        <Sentry.ErrorBoundary fallback={<CrashScreen />}>
          <Outlet />
        </Sentry.ErrorBoundary>
      }
    >
      <Route element={<InitialDataLoad />} errorElement={<CrashScreen />}>
        <Route path="/" element={<IndexReroute />} />
        <Route element={<RequireVendorAuth />}>
          <Route element={<AppLayout />}>
            <Route element={<AuthDataLoad />}>
              <Route element={<RequireRoles allowedRoles={[Role.admin]} />}>
                <Route path="dashboard" element={<div>admin dashboard</div>} />
                <Route path="lista" element={<VendorListContainer />} />
                <Route path="lista/:vendorId/rezultati" element={<Answers />} />
                <Route path="zahtevi" element={<VendorRequests />} />
                <Route path="vendor/:vendorId" element={<VerificationWrapper />} />
              </Route>
              <Route element={<RequireRoles allowedRoles={[Role.vendor, Role.consultant, Role.company]} />}>
                <Route path="profil" element={<VendorStatusCheck />} />
                <Route
                  path="profil-kompanije/upitnici/upitnik/vendor-ai-zrelost"
                  element={<AIQuestionnaire />}
                />
                <Route
                  path="profil-kompanije/upitnici/upitnik/vendor-ai-zrelost/rezultati"
                  element={<AIResults />}
                />
                <Route path="tenderi" element={<BidList />} />
                <Route path="tenderi/:bidId" element={<AcceptedBidDetails />} />
                <Route path="ponude" element={<MyOffersContainer />} />
                <Route path="ponuda/:tenderId" element={<ProposalPage />} />
              </Route>
            </Route>
          </Route>
          <Route path="*" element={<PageNotFound />} />
        </Route>
        <Route path="aktivacija" element={<Activation />} />
        <Route path="registracija" element={<Register />} />
        <Route path="naslovna" element={<LandingPage />} />
        <Route path="/auth" element={<AuthLayout />}>
          <Route element={<RedirectIfAuth />}>
            <Route index element={<LandingPage />} />
            <Route path="naslovna" element={<LandingPage />} />
            <Route path="login" element={<Login />} />
            <Route path="confirm" element={<ConfirmSignUp />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="registracija" element={<Register />} />
            <Route path="set-password" element={<SetPassword />} />
            <Route path="reset-password" element={<ResetPassword />} />
            <Route path="*" element={<Login />} />
          </Route>
        </Route>
        <Route path="*" element={<PageNotFound />} />
      </Route>
    </Route>,
  ),
  { basename: '/vendori' },
)

const App: React.FC = (): JSX.Element => {
  const dispatch = useAppDispatch()
  const { errorMessage } = useAppSelector((state) => state.app)

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <RouterProvider router={router} fallbackElement={<div />} />
      <ThemeSnackbar
        snackbarOpen={!!errorMessage}
        variant={SNACKBAR_VARIANTS.error}
        onClose={() => {
          dispatch(setErrorMessage(''))
        }}
        message={errorMessage}
      />
    </ThemeProvider>
  )
}

export default App
